import { INDEX_ROBOTS_DIRECTIVE } from "~/constants/robots";
import { BaseCategory } from "~/types/baseCategory";
import type { ICategory, ICategoryPage } from "~/types/category";
import type { ICategorySeoData, IEventSeoData } from "~/types/seo";

export interface ICategoryPageSeoData extends ICategorySeoData {
  h1: string;
  ogTitle: string;
  ogDescription: string;
}

export const categoryPageSeoBySlug: Record<BaseCategory, ICategoryPageSeoData> =
  {
    [BaseCategory.Sports]: {
      h1: "What are the latest sports prediction market odds?",
      seoTitle: "Sports Prediction Markets — Odds Across Every Platform",
      seoDescription:
        "Sports predictions & odds. Compare market forecasts on games, tournaments, and athletic outcomes across platforms.",
      ogTitle: "Sports Prediction Markets — Odds Across Every Platform",
      ogDescription:
        "Sports predictions & odds. Compare market forecasts on games, tournaments, and athletic outcomes across platforms.",
    },
    [BaseCategory.Politics]: {
      h1: "What are the latest political prediction market odds?",
      seoTitle: "Political Prediction Markets — Odds & Probabilities",
      seoDescription:
        "Election & political predictions. Compare market odds and forecasts on political outcomes.",
      ogTitle: "Political Prediction Markets — Odds & Probabilities",
      ogDescription:
        "Election & political predictions. Compare market odds and forecasts on political outcomes.",
    },
    [BaseCategory.Financials]: {
      h1: "What are financial prediction markets pricing in?",
      seoTitle: "Financial Prediction Markets — Fed, Rates & Markets Odds",
      seoDescription:
        "Financial predictions & market odds. Compare forecasts on stock performance and economic indicators.",
      ogTitle: "Financial Prediction Markets — Fed, Rates & Markets Odds",
      ogDescription:
        "Financial predictions & market odds. Compare forecasts on stock performance and economic indicators.",
    },
    [BaseCategory.Elections]: {
      h1: "Who's leading in election prediction markets?",
      seoTitle: "Election Prediction Markets — Odds for 2026 and Beyond",
      seoDescription:
        "Election predictions & political odds. See where prediction markets stand on electoral outcomes.",
      ogTitle: "Election Prediction Markets — Odds for 2026 and Beyond",
      ogDescription:
        "Election predictions & political odds. See where prediction markets stand on electoral outcomes.",
    },
    [BaseCategory.Crypto]: {
      h1: "What are the latest crypto prediction market odds?",
      seoTitle: "Crypto Prediction Markets — Price Predictions & Odds",
      seoDescription:
        "Cryptocurrency predictions & market odds. Compare forecasts on crypto trends and blockchain developments.",
      ogTitle: "Crypto Prediction Markets — Price Predictions & Odds",
      ogDescription:
        "Cryptocurrency predictions & market odds. Compare forecasts on crypto trends and blockchain developments.",
    },
    [BaseCategory.Entertainment]: {
      h1: "What are entertainment prediction markets pricing in?",
      seoTitle:
        "Entertainment Prediction Markets — Odds for Awards, Shows & More",
      seoDescription:
        "Entertainment predictions & odds. Market forecasts on awards, releases, and entertainment trends.",
      ogTitle:
        "Entertainment Prediction Markets — Odds for Awards, Shows & More",
      ogDescription:
        "Entertainment predictions & odds. Market forecasts on awards, releases, and entertainment trends.",
    },
    [BaseCategory.Economics]: {
      h1: "What are economics prediction markets pricing in?",
      seoTitle:
        "Economics Prediction Markets — Growth, Inflation & Policy Odds",
      seoDescription:
        "Economic predictions & market forecasts. Compare odds on inflation, growth, and macroeconomic trends.",
      ogTitle: "Economics Prediction Markets — Growth, Inflation & Policy Odds",
      ogDescription:
        "Economic predictions & market forecasts. Compare odds on inflation, growth, and macroeconomic trends.",
    },
    [BaseCategory.Companies]: {
      h1: "What are company prediction markets pricing in?",
      seoTitle: "Companies Prediction Markets — Earnings, M&A & Stock Odds",
      seoDescription:
        "Business predictions & economics. Compare market forecasts on company performance and market trends.",
      ogTitle: "Companies Prediction Markets — Earnings, M&A & Stock Odds",
      ogDescription:
        "Business predictions & economics. Compare market forecasts on company performance and market trends.",
    },
    [BaseCategory.ScienceAndTechnology]: {
      h1: "What are science and technology prediction markets pricing in?",
      seoTitle:
        "Science and Technology Prediction Markets — AI, Space & Research Odds",
      seoDescription:
        "Science & technology predictions. Market odds on product launches, research breakthroughs, and industry trends.",
      ogTitle:
        "Science and Technology Prediction Markets — AI, Space & Research Odds",
      ogDescription:
        "Science & technology predictions. Market odds on product launches, research breakthroughs, and industry trends.",
    },
    [BaseCategory.Transportation]: {
      h1: "What are transportation prediction markets pricing in?",
      seoTitle:
        "Transportation Prediction Markets — Aviation, Auto & Mobility Odds",
      seoDescription:
        "Transportation prediction market odds. Track aviation, autos, shipping, transit projects, and mobility policy events.",
      ogTitle:
        "Transportation Prediction Markets — Aviation, Auto & Mobility Odds",
      ogDescription:
        "Transportation prediction market odds. Track aviation, autos, shipping, transit projects, and mobility policy events.",
    },
    [BaseCategory.Health]: {
      h1: "What are health prediction markets pricing in?",
      seoTitle: "Health Prediction Markets — FDA, Disease & Healthcare Odds",
      seoDescription:
        "Healthcare predictions & medical outcomes. Market odds on health trends and medical breakthroughs.",
      ogTitle: "Health Prediction Markets — FDA, Disease & Healthcare Odds",
      ogDescription:
        "Healthcare predictions & medical outcomes. Market odds on health trends and medical breakthroughs.",
    },
    [BaseCategory.World]: {
      h1: "What are world prediction markets pricing in?",
      seoTitle: "World Prediction Markets — Global Event Odds",
      seoDescription:
        "Geopolitical predictions & conflict forecasts. Compare market odds on international events.",
      ogTitle: "World Prediction Markets — Global Event Odds",
      ogDescription:
        "Geopolitical predictions & conflict forecasts. Compare market odds on international events.",
    },
    [BaseCategory.ClimateAndWeather]: {
      h1: "What are climate and weather prediction markets pricing in?",
      seoTitle:
        "Climate and Weather Prediction Markets — Storm, Temperature & Climate Odds",
      seoDescription:
        "Climate & environment predictions. Compare market forecasts on climate events and environmental outcomes.",
      ogTitle:
        "Climate and Weather Prediction Markets — Storm, Temperature & Climate Odds",
      ogDescription:
        "Climate & environment predictions. Compare market forecasts on climate events and environmental outcomes.",
    },
    [BaseCategory.Commodities]: {
      h1: "What are commodities prediction markets pricing in?",
      seoTitle: "Commodities Prediction Markets — Oil, Gold & Crop Odds",
      seoDescription:
        "Commodities prediction market odds. Track oil, gold, natural gas, crop prices, supply shocks, and commodity market events.",
      ogTitle: "Commodities Prediction Markets — Oil, Gold & Crop Odds",
      ogDescription:
        "Commodities prediction market odds. Track oil, gold, natural gas, crop prices, supply shocks, and commodity market events.",
    },
    [BaseCategory.Mentions]: {
      h1: "What are mentions prediction markets pricing in?",
      seoTitle: "Mentions Prediction Markets — Media, Speech & Attention Odds",
      seoDescription:
        "Mentions prediction market odds. Track public remarks, media appearances, social attention, and who-says-what events.",
      ogTitle: "Mentions Prediction Markets — Media, Speech & Attention Odds",
      ogDescription:
        "Mentions prediction market odds. Track public remarks, media appearances, social attention, and who-says-what events.",
    },
    [BaseCategory.Social]: {
      h1: "What are social prediction markets pricing in?",
      seoTitle: "Social Prediction Markets — Trends, Platforms & Culture Odds",
      seoDescription:
        "Social prediction market odds. Track social media milestones, viral trends, platform changes, creator events, and online culture.",
      ogTitle: "Social Prediction Markets — Trends, Platforms & Culture Odds",
      ogDescription:
        "Social prediction market odds. Track social media milestones, viral trends, platform changes, creator events, and online culture.",
    },
    [BaseCategory.Education]: {
      h1: "What are education prediction markets pricing in?",
      seoTitle: "Education Prediction Markets — Schools, Policy & Testing Odds",
      seoDescription:
        "Education prediction market odds. Track school policy, admissions, testing, student debt, higher education, and campus events.",
      ogTitle: "Education Prediction Markets — Schools, Policy & Testing Odds",
      ogDescription:
        "Education prediction market odds. Track school policy, admissions, testing, student debt, higher education, and campus events.",
    },
    [BaseCategory.Other]: {
      h1: "What are other prediction markets pricing in?",
      seoTitle: "Other Prediction Markets — Odds Across Emerging Categories",
      seoDescription:
        "Prediction market odds in emerging and uncategorized markets. Track unusual events, niche forecasts, and new market themes.",
      ogTitle: "Other Prediction Markets — Odds Across Emerging Categories",
      ogDescription:
        "Prediction market odds in emerging and uncategorized markets. Track unusual events, niche forecasts, and new market themes.",
    },
  };

export const getCategoryPageSeo = (slug?: string | null) => {
  if (!slug || !(slug in categoryPageSeoBySlug)) {
    return undefined;
  }

  return categoryPageSeoBySlug[slug as BaseCategory];
};

export const buildCategorySeoData = (
  pageSeo: ICategoryPage["seo"] | undefined,
  fallbackSeo: ICategoryPageSeoData | ICategory["seo"]
): IEventSeoData => ({
  seoTitle: pageSeo?.seoTitle || fallbackSeo?.seoTitle || "",
  seoDescription: fallbackSeo?.seoDescription || pageSeo?.seoDescription || "",
  ogTitle: fallbackSeo?.ogTitle,
  ogDescription: fallbackSeo?.ogDescription,
  canonicalUrl: pageSeo?.canonicalUrl,
  robotsDirective: pageSeo?.robotsDirective || INDEX_ROBOTS_DIRECTIVE,
  isIndexable: pageSeo?.isIndexable,
  sitemapEligible: pageSeo?.sitemapEligible,
});
